import { motion } from "framer-motion";
import { Trophy, Activity } from "lucide-react";
import { useLightbox } from "./LightboxContext";

const basketballPosters = [
  {
    player: "Kobe Bryant",
    jersey: "24",
    team: "Los Angeles Lakers",
    tag: "Mamba mentality",
    accent: "#552583",
    accentText: "#FDB927",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/jrvvy8et_kobe.jpg",
  },
  {
    player: "Derrick Rose",
    jersey: "01",
    team: "Chicago Bulls",
    tag: "Windy city",
    accent: "#CE1141",
    accentText: "#FFFFFF",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/9tw72klc_rose.jpg",
  },
  {
    player: "Stephen Curry",
    jersey: "30",
    team: "Golden State Warriors",
    tag: "Splash",
    accent: "#1D428A",
    accentText: "#FFC72C",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/f5p8kpfw_currz%20fade.jpg",
  },
  {
    player: "Victor Wembanyama",
    jersey: "01",
    team: "San Antonio Spurs",
    tag: "Generational",
    accent: "#EC6F1B",
    accentText: "#FFFFFF",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/wh1i6x2r_fade.jpg",
  },
];

const footballPosters = [
  {
    player: "Thierry Henry",
    jersey: "14",
    team: "Arsenal FC",
    tag: "Va-va-voom",
    accent: "#EF0107",
    accentText: "#FFFFFF",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/5poriyyw_image.png",
  },
  {
    player: "Marc-André ter Stegen",
    jersey: "01",
    team: "FC Barcelona",
    tag: "Last line",
    accent: "#0A0A0A",
    accentText: "#2DD4D4",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/0l5yyjj4_GOLMAN.jpg",
  },
  {
    player: "Dušan Vlahović",
    jersey: "09",
    team: "Juventus FC",
    tag: "Vecchia signora",
    accent: "#111111",
    accentText: "#FFFFFF",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/3qasmawh_vlahovic.jpg",
  },
];

function PosterCard({ p, i, size = "default" }) {
  const { open } = useLightbox();
  const captionClasses =
    size === "large"
      ? "mt-6 md:mt-8 flex items-end justify-between gap-6"
      : "mt-5 flex items-end justify-between gap-6";
  const titleClasses =
    size === "large"
      ? "mt-3 font-display italic text-3xl md:text-5xl leading-tight"
      : "mt-2 font-display italic text-2xl md:text-3xl leading-tight";
  const teamClasses =
    size === "large"
      ? "mt-2 text-base md:text-lg text-[var(--ink-soft)]"
      : "mt-1 text-sm text-[var(--ink-soft)]";
  const jerseyClasses =
    size === "large"
      ? "font-sans-display text-6xl md:text-8xl leading-none tabular-nums"
      : "font-sans-display text-4xl md:text-5xl leading-none tabular-nums";

  return (
    <motion.figure
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: i * 0.08 }}
      className="group relative"
      data-testid={`sport-poster-${p.player
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")}`}
    >
      <div
        className={`relative w-full overflow-hidden bg-[#ededeb] cursor-zoom-in ${
          size === "large" ? "" : "aspect-square"
        }`}
        onClick={() => open(p.img, `${p.player} — ${p.team}, sport graphic edit`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open(p.img, `${p.player} — ${p.team}, sport graphic edit`);
          }
        }}
      >
        {size === "large" ? (
          <img
            src={p.img}
            alt={`${p.player} — ${p.team}, sport graphic edit`}
            className="block w-full h-auto transition-transform duration-[1400ms] ease-out group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          <img
            src={p.img}
            alt={`${p.player} — ${p.team}, sport graphic edit`}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
            loading="lazy"
          />
        )}
        <span
          className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.3em] px-2.5 py-1 backdrop-blur-sm"
          style={{
            background: `${p.accent}E6`,
            color: p.accentText,
            borderLeft: `3px solid ${p.accentText}`,
          }}
        >
          #{p.jersey} · {p.team.split(" ").slice(-1)[0]}
        </span>
        <span
          className="absolute bottom-0 left-0 right-0 px-6 py-4 text-white font-display italic text-2xl md:text-3xl translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out"
          style={{
            background: `linear-gradient(to top, ${p.accent}F2, ${p.accent}00)`,
          }}
        >
          {p.tag}.
        </span>
      </div>

      <figcaption className={captionClasses}>
        <div>
          <div
            className="font-mono text-[10px] uppercase tracking-[0.3em]"
            style={{ color: p.accent }}
          >
            Edit · {String(i + 1).padStart(2, "0")}
          </div>
          <div className={titleClasses}>{p.player}</div>
          <div className={teamClasses}>{p.team}</div>
        </div>
        <span className={jerseyClasses} style={{ color: p.accent }}>
          {p.jersey}
        </span>
      </figcaption>
    </motion.figure>
  );
}

export default function SportPosters() {
  return (
    <div className="mb-20 md:mb-28" data-testid="sport-posters">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
        <div className="flex items-center gap-4">
          <span className="w-10 h-10 border border-[var(--ink)] flex items-center justify-center">
            <Trophy className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <h3 className="font-sans-display text-3xl md:text-5xl tracking-[-0.02em] font-light uppercase">
            Sport edits
          </h3>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
          §03.a — Player tributes · basketball & football
        </span>
      </div>

      {/* Basketball sub-group */}
      <div className="flex items-center gap-4 mb-6 md:mb-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-soft)]">
          01 — Basketball
        </span>
        <span className="flex-1 h-px bg-[var(--ink)] opacity-15" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {basketballPosters.map((p, i) => (
          <PosterCard key={p.player} p={p} i={i} />
        ))}
      </div>

      {/* Football sub-group — ter Stegen featured large, others underneath */}
      <div className="flex items-center gap-4 mt-16 md:mt-20 mb-6 md:mb-8">
        <Activity className="w-4 h-4" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-soft)]">
          02 — Football
        </span>
        <span className="flex-1 h-px bg-[var(--ink)] opacity-15" />
      </div>
      {/* Featured large — ter Stegen (index 1) */}
      <div className="max-w-[920px] mx-auto mb-10 md:mb-14">
        <PosterCard p={footballPosters[1]} i={0} size="large" />
      </div>
      {/* Henry + Vlahović underneath */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
        <PosterCard p={footballPosters[0]} i={1} />
        <PosterCard p={footballPosters[2]} i={2} />
      </div>
    </div>
  );
}
